import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Brain, Users, Key, Network, BarChart2, UserPlus, Settings, Inbox, ClipboardCheck, Compass, GraduationCap, Zap } from '../icons';
import { SidebarLayout } from './SidebarLayout';
import { RoleSwitcher } from '../RoleSwitcher';
import { useAuth } from '../../contexts/AuthContext';
import { isUserPartner } from '../../lib/auth';
import { LoadingSpinner } from '../LoadingSpinner';
import { usePartnerNotifications } from '../../hooks/usePartnerNotifications';
import type { SidebarItem } from './Sidebar';

export function PartnerLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { counts, markSeen } = usePartnerNotifications();
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    isUserPartner(user.id).then((isPartner) => {
      if (!isPartner) {
        navigate('/');
        return;
      }
      setAuthorized(true);
      setChecking(false);
    });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user || !authorized) return;
    if (location.pathname === '/partner/programs') {
      markSeen('programs');
    }
  }, [location.pathname, user, authorized, markSeen]);

  if (authLoading || checking || !authorized) {
    return <LoadingSpinner message="Loading..." />;
  }

  const partnerItems: SidebarItem[] = [
    { label: 'Dashboard', path: '/partner', icon: Brain },
    { label: 'Coaches', path: '/partner/coaches', icon: Users },
    { label: 'Teams', path: '/partner/teams', icon: Network },
    { label: 'Shared Results', path: '/partner/shared-results', icon: Inbox },
    { label: 'Assessment Codes', path: '/partner/codes', icon: Key },
    { label: 'Programs', path: '/partner/programs', icon: ClipboardCheck, badgeCount: counts.programs },
    { label: 'Pathways', path: '/partner/pathways', icon: Compass },
    { label: 'Free Profile Referrals', path: '/partner/free-profile-referrals', icon: UserPlus },
    { label: 'Analytics', path: '/partner/analytics', icon: BarChart2 },
    { label: 'AI Usage', path: '/partner/analytics#nlp-practice', icon: Zap },
    { label: 'Settings', path: '/partner/settings', icon: Settings },
    { label: 'NLP Practice', path: '/partner/nlp-practice', icon: GraduationCap },
  ];

  return (
    <SidebarLayout
      items={partnerItems}
      title="Partner"
      subtitle="Manage coaches & teams"
      headerSlot={<RoleSwitcher currentRole="partner" />}
    />
  );
}
